import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Prompt direction toggle — a small editing convenience for the
 * "נוסח השאלה" textarea in every template form. The toggle flips the
 * textarea's `dir` and `text-align` together; nothing is persisted
 * and the API payload is unchanged.
 *
 * Open-time default: auto-detected from the current content. If the
 * first strong character is Hebrew the toggle starts in RTL; anything
 * else (Latin, digits, placeholders, empty) starts in LTR. Once the
 * user clicks the toggle, their choice sticks until the form closes
 * — the detection does not re-trigger on subsequent typing.
 */
import React, { useEffect, useState } from 'react';
import { Segmented, Tooltip } from 'antd';
import { AlignLeftOutlined, AlignRightOutlined } from '@ant-design/icons';
const HEBREW = /[\u0590-\u05FF]/;
const STRONG = /[A-Za-z\u0590-\u05FF]/;
/** First strong directional character wins. If none is present (digits
 *  only, punctuation only, placeholders only, empty) default to LTR —
 *  that's where authors usually start before typing Hebrew. */
export function detectDir(text) {
    const m = (text || '').match(STRONG);
    if (!m)
        return 'ltr';
    return HEBREW.test(m[0]) ? 'rtl' : 'ltr';
}
/**
 * Hook that owns the prompt direction state. Resets to the detected
 * value every time `open` transitions false → true, so each reopen of
 * the modal starts fresh. User calls `setDir` to override.
 */
export function usePromptDirection(form, fieldName, open) {
    const [dir, setDir] = useState('ltr');
    useEffect(() => {
        if (!open)
            return;
        const current = form.getFieldValue(fieldName) ?? '';
        setDir(detectDir(current));
    }, [open, form, fieldName]);
    return { dir, setDir };
}
/** Icon-only Segmented toggle. AlignRight = RTL (text pinned to the
 *  right, Hebrew reading flow); AlignLeft = LTR. Each option carries a
 *  Hebrew hover tooltip so sighted and keyboard users both get the
 *  explicit label on demand. */
export function PromptDirectionToggle({ dir, onChange }) {
    return (_jsx(Segmented, { size: "small", value: dir, onChange: (v) => onChange(v), options: [
            {
                value: 'rtl',
                icon: _jsx(Tooltip, { title: "\u05D9\u05DE\u05D9\u05DF \u05DC\u05E9\u05DE\u05D0\u05DC", children: _jsx(AlignRightOutlined, {}) }),
            },
            {
                value: 'ltr',
                icon: _jsx(Tooltip, { title: "\u05E9\u05DE\u05D0\u05DC \u05DC\u05D9\u05DE\u05D9\u05DF", children: _jsx(AlignLeftOutlined, {}) }),
            },
        ] }));
}
